import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  IconButton,
} from "@mui/material";
import axios from "axios";
import Header from "components/Header";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import { Add, Delete, Edit, CloudUpload } from "@mui/icons-material";
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const PortfolioCategories = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [iconImagePath, setIconImagePath] = useState("");
  const [websiteImagePath, setWebsiteImagePath] = useState("");
  const [formData, setFormData] = useState({ 
    name: "", 
    url: "", 
    description: "", 
  });
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch categories from the API
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${apiUrl}/portfolio/categories`);
      console.log(response,"thisi sresponse");
      
      setCategories(response.data.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setIsLoading(false);
      setErrorMessage("Error fetching categories");
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Image upload handler
  const handleImageUpload = async (file, setImagePath) => {
    // First validate file size client-side
    const MAX_SIZE = 50 * 1024 * 1024; // 50MB
    if (file.size > MAX_SIZE) {
      setErrorMessage("File is too large. Maximum size is 50MB.");
      setSnackbarOpen(true);
      return;
    }
  
    const formData = new FormData();
    formData.append("image", file);
  
    try {
      const response = await axios.post(`${apiUrl}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          // You can use this to show upload progress
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });
      
      // setImagePath(response.data.filePath);
      setImagePath(response.data.url);
    } catch (error) {
      let errorMsg = "Error uploading image";
      if (error.response) {
        if (error.response.status === 413) {
          errorMsg = "File is too large. Maximum size is 50MB.";
        } else {
          errorMsg = error.response.data.error || errorMsg;
        }
      }
      setErrorMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  const handleFileChange = (event, type) => {
    const selectedImage = event.target.files[0];
    if (selectedImage) {
      if (type === 'icon') {
        handleImageUpload(selectedImage, setIconImagePath);
      } else {
        handleImageUpload(selectedImage, setWebsiteImagePath);
      }
    }
  };

  // Open the form for adding or editing
  const handleOpenModal = (category = null) => {
    setSelectedCategory(category);
    setFormData(category ? { 
      name: category.name,
      url: category.url,
      description: category.description
    } : { 
      name: "", 
      url: "", 
      description: "", 
    });
    setIconImagePath(category?.iconImage || "");
    setWebsiteImagePath(category?.websiteImage || "");
    setOpenModal(true);
    setErrors({});
  };

  // Close the form
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCategory(null);
    setErrors({});
    setIconImagePath("");
    setWebsiteImagePath("");
  };

  // Handle form data change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Form validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Category name is required.";
    }

    if (!formData.url.trim()) {
      newErrors.url = "URL is required.";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required.";
    }

    if (!iconImagePath) {
      newErrors.iconImage = "Icon image is required.";
    }

    if (!websiteImagePath) {
      newErrors.websiteImage = "Website image is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add or update a category
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const categoryData = {
        ...formData,
        iconImage: iconImagePath,
        websiteImage: websiteImagePath
      };

      if (selectedCategory) {
        // Update category
        const response = await axios.put(
          `${apiUrl}/portfolio/categories/${selectedCategory._id}`,
          categoryData
        );
        setCategories(prevData =>
          prevData.map(item => (item._id === selectedCategory._id ? response.data.data : item))
        );
      } else {
        // Create new category
        const response = await axios.post(
          `${apiUrl}/portfolio/categories`,
          categoryData
        );
        setCategories(prevData => [...prevData, response.data.data]);
      }
      handleCloseModal();
    } catch (error) {
      console.error("Error saving category:", error);
      setErrorMessage("Error saving category.");
      setSnackbarOpen(true);
    }
  };

  // Delete a category
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${apiUrl}/portfolio/categories/${id}`);
      setCategories(prevData => prevData.filter(item => item._id !== id));
    } catch (error) {
      console.error("Error deleting category:", error);
      setErrorMessage("Error deleting category.");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const columns = [
    { field: "name", headerName: "Category Name", flex: 0.3 },
    { 
      field: "iconImage", 
      headerName: "Icon", 
      flex: 0.2,
      renderCell: (params) => (
        <img 
          src={params.row.iconImage}
          alt="icon" 
          style={{ width: 40, height: 40, objectFit: 'contain' }} 
        />
      )
    },
    { 
      field: "websiteImage", 
      headerName: "Website Preview", 
      flex: 0.3,
      renderCell: (params) => (
        <img 
          src={params.row.websiteImage}
          alt="website" 
          style={{ width: 80, height: 40, objectFit: 'cover' }} 
        />
      )
    },
    { 
      field: "url", 
      headerName: "URL", 
      flex: 0.4,
      renderCell: (params) => (
        params.row.url

      )
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.3,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleOpenModal(params.row)}
            startIcon={<Edit />}
            sx={{ mr: 1, backgroundColor: "#4d547d" }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => handleDelete(params.row._id)}
            startIcon={<Delete />}
            sx={{ backgroundColor: "white", color: "#191f45" }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="PORTFOLIO CATEGORIES" subtitle="Manage your skill categories" />
      
      <Button 
        variant="contained" 
        color="primary" 
        onClick={() => handleOpenModal(null)}
        startIcon={<Add />}
        sx={{ mb: 3 }}
      >
        Add New Category
      </Button>
  
      <Box mt="40px" height="75vh">
        <DataGrid
          loading={isLoading}
          getRowId={(row) => row._id}
          rows={categories || []}
          columns={columns}
          pageSize={5}
          sx={{ backgroundColor: "#191f45", color: "white" }}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{
            toolbar: { searchInput, setSearchInput, setSearch },
          }}
        />
      </Box>

      {/* Add/Edit Category Dialog */}

      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="md">
        <DialogTitle>{selectedCategory ? "Edit Category" : "Add New Category"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                margin="normal"
                label="Category Name"
                fullWidth
                variant="outlined"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
              />

              <TextField
                margin="normal"
                label="URL"
                fullWidth
                variant="outlined"
                name="url"
                value={formData.url}
                onChange={handleChange}
                error={!!errors.url}
                helperText={errors.url}
              />

              <TextField
                margin="normal"
                label="Description"
                fullWidth
                variant="outlined"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={4}
                error={!!errors.description}
                helperText={errors.description}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Icon Image
                </Typography>
                {errors.iconImage && (
                  <Typography color="error" variant="body2">
                    {errors.iconImage}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {iconImagePath && (
                    <img 
                      src={iconImagePath}
                      alt="icon preview" 
                      style={{ width: 60, height: 60, objectFit: 'contain' }} 
                    />
                  )}
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUpload />}
                    sx={{ backgroundColor: "#4d547d" }}
                  >
                    Upload Icon
                    <VisuallyHiddenInput 
                      type="file" 
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'icon')} 
                    />
                  </Button>
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Website Image
                </Typography>
                {errors.websiteImage && (
                  <Typography color="error" variant="body2">
                    {errors.websiteImage}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {websiteImagePath && (
                    <img 
                      src={websiteImagePath}
                      alt="website preview" 
                      style={{ width: 120, height: 60, objectFit: 'cover' }} 
                    />
                  )}
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUpload />}
                    sx={{ backgroundColor: "#4d547d" }}
                  >
                    Upload Website Image
                    <VisuallyHiddenInput 
                      type="file" 
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'website')} 
                    />
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ backgroundColor: "#4d547d" }}
          >
            {selectedCategory ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PortfolioCategories;